/**
 * Scorecard Store
 *
 * Holds the legacy, front-end-only scorecard metrics that back the old Table Builder scorecard view.
 * Metrics are persisted at the WORKSHEET level (minus the transient display fields captured at runtime),
 * and legacy metrics from older worksteps are normalized to the parameterized-stat shape on rehydrate.
 */

import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { normalizeScorecardState } from '@/tableBuilder/scorecard.utilities';
import { type ActionsFactory, type StoreInstance, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ScorecardMetric } from '@/tableBuilder/scorecard.types';

export interface ScorecardState {
  /** The scorecard metrics currently displayed in the Table Builder scorecard view. */
  metrics: ScorecardMetric[];
}

/** Persisted shape of the scorecard state (same as {@link ScorecardState}; transient display fields are stripped in {@link dehydrateScorecardState}). */
export type ScorecardDehydratedState = ScorecardState;

type ScorecardValueResultPayload = Pick<ScorecardMetric, 'metricId'> & {
  valueResult: string;
  backgroundColor: string;
  foregroundColor: string;
};

export interface ScorecardActions {
  /** Adds a metric, or replaces the existing metric with the same `metricId`. */
  addMetric: (metric: ScorecardMetric) => void;
  /** Removes a metric by its `metricId`. No-op if the metric doesn't exist. */
  removeMetric: (metricId: string) => void;
  /** Captures the formatted value and threshold colors from a metric computation. No-op for unknown `metricId`s. */
  setValueResult: (payload: ScorecardValueResultPayload) => void;
  /** Remaps metric `itemId`s after an asset swap (keys are swapped-out ids, values are swapped-in ids). */
  swapItems: (swaps: Record<string, string>) => void;
  /** Replaces state from a persisted snapshot, normalizing legacy metrics to the parameterized-stat shape. */
  rehydrate: (dehydratedState: RehydratedScorecardState) => void;
  reset: () => void;
}

/** A metric as persisted by older worksteps, before stats were parameterized (`formula`/`timeUnits` instead of `stat`). */
type LegacyScorecardMetric = Partial<ScorecardMetric> & {
  formula?: string;
  timeUnits?: string | null;
};

/** Snapshot accepted by {@link ScorecardActions.rehydrate}; metrics may be in the current or the legacy persisted shape. */
export interface RehydratedScorecardState {
  metrics?: LegacyScorecardMetric[];
}

/**
 * The singleton bridge instance: a Zustand store augmented with the legacy `metrics` getter so non-React
 * consumers (action files) can keep reading `sqScorecardStore.metrics`. New code should prefer
 * `sqScorecardStore.getState().metrics`.
 */
export type LegacyScorecardStore = StoreInstance<ScorecardState, ScorecardActions> & {
  readonly metrics: ScorecardMetric[];
};

const initialState: ScorecardState = Object.freeze({
  metrics: [],
});

export const dehydrateScorecardState = (state: ScorecardState): ScorecardDehydratedState => ({
  metrics: state.metrics.map((metric) => {
    const dehydratedMetric = structuredClone(metric) as ScorecardMetric & {
      valueResult?: string;
      foregroundColor?: string;
      backgroundColor?: string;
    };

    delete dehydratedMetric.valueResult;
    delete dehydratedMetric.foregroundColor;
    delete dehydratedMetric.backgroundColor;

    return dehydratedMetric;
  }),
});

const scorecardActionsFactory: ActionsFactory<ScorecardState, ScorecardActions> = (set, _get, resetState) => ({
  addMetric: (metric) =>
    set((draft) => {
      const nextMetric = structuredClone(metric);
      const index = draft.metrics.findIndex((existingMetric) => existingMetric.metricId === nextMetric.metricId);
      if (index < 0) {
        draft.metrics.push(nextMetric);
      } else {
        draft.metrics[index] = nextMetric;
      }
    }),

  removeMetric: (metricId) =>
    set((draft) => {
      draft.metrics = draft.metrics.filter((metric) => metric.metricId !== metricId);
    }),

  setValueResult: ({ metricId, valueResult, backgroundColor, foregroundColor }) =>
    set((draft) => {
      const metric = draft.metrics.find((existingMetric) => existingMetric.metricId === metricId);
      if (!metric) {
        return;
      }

      Object.assign(metric, {
        valueResult,
        backgroundColor,
        foregroundColor,
      });
    }),

  swapItems: (swaps) =>
    set((draft) => {
      draft.metrics.forEach((metric) => {
        const swappedId = swaps[metric.itemId];
        if (swappedId) {
          metric.itemId = swappedId;
        }
      });
    }),

  rehydrate: (dehydratedState) => {
    set(() => normalizeScorecardState(dehydratedState));
  },

  reset: resetState,
});

const STORE_NAME = 'Scorecard';

const [ScorecardStoreProvider, useScorecardStoreInstance, useScorecardSelector] = createStore(
  STORE_NAME,
  initialState,
  scorecardActionsFactory,
);

export { ScorecardStoreProvider };

export const useScorecardMetrics = () => useScorecardSelector((state) => state.metrics);
export const useScorecardActions = () => useScorecardStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateScorecardStore() {
  return createStoreInstance(initialState, scorecardActionsFactory, { storeName: STORE_NAME });
}

export const usePersistScorecardStore = () => {
  const register = useRegisterPersistedStore();
  const store = useScorecardStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqScorecardStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateScorecardState,
    });
  }, [register, store]);
};
